import { ListItemIcon, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DownloadDialog from 'components/Modals/implementations/DownloadDialog';
import DownloadIcon from 'assets/icons/DownloadIcon';
import { AnalyticsEventLocation } from 'services/analytics/entities';
import { useAuthStore, useEmailConfirmationDialog } from 'services/hooks';
import { selectSong } from 'services/store/newSongs/selectors';
import { selectEmailConfirmed } from 'services/store/user/selectors';
import useGetSongGenre from 'services/hooks/songs/useGetSongGenre';
import { locations } from 'services/routes/locations';
import { DownloadInfo } from 'types';
import { SongType } from 'services/api/entities';
import { events } from 'services/analytics';
import useAnalytics from 'services/hooks/analytics';
import { StyledMenuItem } from './styled';

type PropsType = {
  songId: string;
  songType: SongType;
  location: AnalyticsEventLocation;
  addToLibrary: () => void;
};

const DownloadButton = ({
  songId,
  songType,
  location,
  addToLibrary,
}: PropsType) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { authStore } = useAuthStore();
  const { track } = useAnalytics();
  const { isLoggedIn } = authStore;

  const isEmailConfirmed = useSelector(selectEmailConfirmed);
  const {
    onEmailConfirmationDialogShouldOpen,
    displayEmailConfirmationDialog,
  } = useEmailConfirmationDialog();

  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const song = useSelector(selectSong(songId));
  const { genre } = useGetSongGenre(song);

  const shouldDisplayEmailConfirmationDialog = useMemo(() => {
    if (!isLoggedIn) {
      return false;
    }
    return !isEmailConfirmed;
  }, [isLoggedIn, isEmailConfirmed]);

  const handleDownloadClick = () => {
    if (!isLoggedIn) {
      navigate(locations.general.register.pathname(''));
      return;
    }
    if (shouldDisplayEmailConfirmationDialog) {
      onEmailConfirmationDialogShouldOpen();
      return;
    }
    setIsDownloadModalOpen(true);
  };

  const analyticsTrackDownload = useCallback(
    (downloadInfo: DownloadInfo) => {
      const isAiGenerated = songType === SongType.AI_GENERATED;

      track(
        events.downloadEvent(
          song?.title || '',
          genre,
          downloadInfo,
          isAiGenerated,
          songType,
          location
        )
      );
    },
    [track, song?.title, genre, songType, location]
  );

  const onModalClose = () => setIsDownloadModalOpen(false);

  return (
    <>
      <StyledMenuItem onClick={handleDownloadClick}>
        <ListItemIcon>
          <DownloadIcon />
        </ListItemIcon>
        <Typography>{t('general:action:download')}</Typography>
      </StyledMenuItem>

      {displayEmailConfirmationDialog()}
      <DownloadDialog
        songId={songId}
        songType={songType}
        isOpen={isDownloadModalOpen}
        onClose={onModalClose}
        addToLibrary={addToLibrary}
        analyticsTrackDownload={analyticsTrackDownload}
      />
    </>
  );
};

export default DownloadButton;
